/**
 * Detects when the user landed from an email link (via URL params) and logs
 * a dedicated event to the custom Firestore logs collection for attribution.
 */

import { logEvent } from "./loggingService";

const EMAIL_SOURCE_PARAM = "src";
const EMAIL_TYPE_PARAM = "emailType";
const CAMPAIGN_PARAM = "campaign";

const EMAIL_SOURCE_VALUE = "email";

export type EmailArrivalParams = {
  source: string;
  emailType: string;
  campaign?: string;
  landingPath: string;
  returnTo?: string;
  chatId?: string;
  detectedAt: string;
};

/**
 * Parses the current URL for email-source query params (src=email, emailType, campaign).
 * Returns a normalized payload for logging, or null if no email source is present.
 */
export function parseEmailArrivalFromUrl(): EmailArrivalParams | null {
  if (typeof window === "undefined") return null;

  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);

  const src = params.get(EMAIL_SOURCE_PARAM);
  if (src !== EMAIL_SOURCE_VALUE) return null;

  const emailType = params.get(EMAIL_TYPE_PARAM) ?? "unknown";
  const campaign = params.get(CAMPAIGN_PARAM) ?? undefined;
  const returnTo = params.get("returnTo") ?? undefined;

  const chatId = getChatIdFromPath(pathname);

  return {
    source: EMAIL_SOURCE_VALUE,
    emailType,
    ...(campaign ? { campaign } : {}),
    landingPath: pathname,
    ...(returnTo ? { returnTo } : {}),
    ...(chatId ? { chatId } : {}),
    detectedAt: new Date().toISOString(),
  };
}

function getChatIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/chats\/([^/]+)$/);
  return match ? match[1] : undefined;
}

/**
 * If the current URL indicates the user arrived from an email link, logs
 * a `user_entered_from_email` event to the Firestore logs collection with
 * structured params. Safe to call on every relevant page load; duplicates
 * are acceptable per product decision.
 */
export async function logEmailArrivalIfPresent(): Promise<void> {
  const payload = parseEmailArrivalFromUrl();
  if (!payload) return;

  await logEvent("user_entered_from_email", payload as Record<string, unknown>);
}
